// Transcript screen — interactive text panel for creating/editing reels.
//
// Each word is a clickable span with absolute timestamp data attributes.
// User selects text → derive [t_start, t_end] from first/last word → create clip.
// Existing clips highlight the spans of words they cover; clicking one enters
// "edit mode" — re-selecting then updates that clip's boundaries.
//
// 4-hour episodes contain 30k–45k words. To stay responsive we:
//   • load word arrays lazily per language (ES/RU) from a dedicated endpoint;
//   • render chapters lazily — a chapter only mounts its word spans once it
//     scrolls near the viewport (placeholder reserves space until then);
//   • derive selection bounds from the selection's own range, not a full-DOM
//     querySelectorAll('.tr-word') on every mouseup.
//
// Range selection is the NATIVE browser text selection only — select words and
// the [t_start, t_end] is derived from the first/last selected word. (The old
// draggable start/end handles were removed: they were glitchy and redundant.)

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useStore } from '../state/store.js';
import { fmtClock, fmtDur } from '../components/ui.jsx';

const STATUS_LABELS = {
  draft: 'черновик',
  queued: 'в очереди',
  published: 'опубликован',
  failed: 'ошибка',
};

const ROMAN = ['I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII','XIII','XIV','XV','XVI','XVII','XVIII','XIX','XX'];

function WordSpans({ words, wordClipMap, activeClipId, onWordClick, onWordDoubleClick }) {
  return words.map((w) => {
    const clipsForWord = wordClipMap.get(w._idx);
    const inClip = clipsForWord?.length > 0;
    const primaryClip = clipsForWord?.[0];
    const isActive = primaryClip && activeClipId === primaryClip.id;
    const failed = primaryClip?.status === 'failed';
    const hue = primaryClip?._hue;
    const cls = [
      'tr-word',
      inClip ? 'in-clip' : '',
      isActive ? 'in-clip-active' : '',
      failed ? 'in-clip-failed' : '',
    ].filter(Boolean).join(' ');
    const style = inClip && hue
      ? { '--clip-fill': hue.fill, '--clip-line': hue.line }
      : undefined;
    return (
      <span
        key={w._idx}
        className={cls}
        style={style}
        data-start={w.start}
        data-end={w.end}
        data-clip-id={primaryClip?.id}
        onClick={(e) => {
          if (inClip && primaryClip) {
            e.stopPropagation();
            onWordClick(primaryClip.id);
          }
        }}
        onDoubleClick={(e) => {
          if (inClip && primaryClip) {
            e.stopPropagation();
            onWordDoubleClick(primaryClip.id);
          }
        }}
        title={primaryClip ? `${primaryClip.title} · ${STATUS_LABELS[primaryClip.status] || primaryClip.status}` : ''}
      >
        {w.text}{' '}
      </span>
    );
  });
}

// Lazily mounts the word spans of a chapter only when it scrolls near the
// viewport. Until then a placeholder of estimated height keeps scroll position
// stable. Once mounted it stays mounted (cheap to keep, expensive to thrash).
const TranscriptChapter = React.memo(function TranscriptChapter({
  chapter, words, idx, wordClipMap, activeClipId, onWordClick, onWordDoubleClick,
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(idx < 2); // first chapters render immediately

  useEffect(() => {
    if (shown || !ref.current) return undefined;
    const el = ref.current;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setShown(true);
        obs.disconnect();
      }
    }, { rootMargin: '800px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [shown]);

  // Rough placeholder height: ~10 words per line, ~28px per line.
  const placeholderHeight = Math.max(80, Math.ceil(words.length / 10) * 28);

  return (
    <section className="tr-chapter" ref={ref}>
      <header className="tr-chapter-head">
        {chapter ? (
          <>
            <span className="tr-chapter-roman">{ROMAN[idx] || idx + 1}</span>
            <div className="tr-chapter-title">
              <div className="tr-chapter-title-es">{chapter.title}</div>
              {chapter.title_ru && <div className="tr-chapter-title-ru">{chapter.title_ru}</div>}
            </div>
            <span className="chip chip-soft">
              {fmtClock(chapter.t_start)} · {fmtDur(chapter.t_end - chapter.t_start)}
            </span>
          </>
        ) : (
          <span className="muted small">вне глав</span>
        )}
      </header>
      {shown ? (
        <p className="tr-paragraph">
          <WordSpans
            words={words}
            wordClipMap={wordClipMap}
            activeClipId={activeClipId}
            onWordClick={onWordClick}
            onWordDoubleClick={onWordDoubleClick}
          />
        </p>
      ) : (
        <div className="tr-paragraph-placeholder" style={{ height: placeholderHeight }} aria-hidden />
      )}
    </section>
  );
}, (prev, next) => {
  if (
    prev.chapter !== next.chapter ||
    prev.words !== next.words ||
    prev.wordClipMap !== next.wordClipMap
  ) return false;
  if (prev.activeClipId === next.activeClipId) return true;

  const hasClip = (clipId) => {
    if (!clipId) return false;
    for (const w of next.words) {
      const clips = next.wordClipMap.get(w._idx);
      if (clips && clips.some((c) => c.id === clipId)) return true;
    }
    return false;
  };
  if (hasClip(prev.activeClipId) || hasClip(next.activeClipId)) return false;
  return true;
});


function ActiveClipCard({ clip, onClose, onOpen, onRename, onDelete }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(clip.title || '');

  useEffect(() => {
    setTitleDraft(clip.title || '');
    setEditingTitle(false);
  }, [clip.id, clip.title]);

  return (
    <div className="tr-selection-card" style={clip._hue ? { borderTop: `3px solid ${clip._hue.line}` } : undefined}>
      <div className="tr-selection-head">🎯 клип активен</div>

      {editingTitle ? (
        <input
          className="tr-clip-title-input"
          autoFocus
          value={titleDraft}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={() => {
            const t = titleDraft.trim();
            if (t && t !== clip.title) onRename(t);
            setEditingTitle(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') { setTitleDraft(clip.title || ''); setEditingTitle(false); }
          }}
        />
      ) : (
        <div
          className="tr-active-title"
          onClick={() => setEditingTitle(true)}
          title="кликни чтобы переименовать"
        >
          «{clip.title || 'без названия'}» ✎
        </div>
      )}

      <div className="muted small" style={{ margin: '6px 0' }}>
        {fmtClock(clip.t_start)} → {fmtClock(clip.t_end)} · {fmtDur(clip.t_end - clip.t_start)}<br/>
        статус: {STATUS_LABELS[clip.status] || clip.status}
      </div>

      <div className="muted small" style={{ marginBottom: 12, fontStyle: 'italic' }}>
        выдели в тексте слева новые границы — клип расширится/сожмётся
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button className="btn btn-dark" onClick={onOpen} style={{ flex: 1, minWidth: 120 }}>
          открыть редактор →
        </button>
        <button className="btn btn-outline" onClick={() => setEditingTitle(true)} title="переименовать">
          ✎
        </button>
        <button
          className="btn btn-outline"
          onClick={onDelete}
          title="удалить клип"
          style={{ color: 'var(--err)', borderColor: 'rgba(168,65,58,0.4)' }}
        >
          🗑
        </button>
      </div>

      <button className="btn-link small" onClick={onClose} style={{ marginTop: 10, display: 'block', textAlign: 'center', width: '100%' }}>
        снять выделение
      </button>
    </div>
  );
}

// Distinct palette so adjacent clips don't blend visually.
const CLIP_HUES = [
  { fill: 'rgba(184,115,46,0.30)',  line: 'rgba(184,115,46,0.85)' },  // amber
  { fill: 'rgba(93,117,86,0.32)',   line: 'rgba(93,117,86,0.85)' },   // sage
  { fill: 'rgba(54,67,116,0.28)',   line: 'rgba(54,67,116,0.85)' },   // indigo
  { fill: 'rgba(168,90,78,0.30)',   line: 'rgba(168,90,78,0.85)' },   // rose
  { fill: 'rgba(122,72,118,0.30)',  line: 'rgba(122,72,118,0.85)' },  // purple
  { fill: 'rgba(74,121,128,0.32)',  line: 'rgba(74,121,128,0.85)' },  // teal
  { fill: 'rgba(201,132,46,0.30)',  line: 'rgba(201,132,46,0.85)' },  // ochre
  { fill: 'rgba(166,86,55,0.30)',   line: 'rgba(166,86,55,0.85)' },   // terracotta
  { fill: 'rgba(112,121,55,0.30)',  line: 'rgba(112,121,55,0.85)' },  // olive
  { fill: 'rgba(48,93,148,0.30)',   line: 'rgba(48,93,148,0.85)' },   // cobalt
];

// Climb from a DOM node to the nearest word span (boundaries can land on the
// text node inside a span or on a sibling span).
function nearestWordEl(node) {
  let el = node && node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  return el && el.closest ? el.closest('.tr-word') : null;
}

export function TranscriptScreen() {
  const { epId } = useParams();
  const navigate = useNavigate();
  const toast = useStore((s) => s.toast);

  const [episode, setEpisode] = useState(null);
  const [meta, setMeta] = useState(null);            // { word_count, word_count_ru, has_ru, ... }
  const [lang, setLang] = useState('es');            // 'es' | 'ru'
  const [wordsByLang, setWordsByLang] = useState({}); // { es: [...], ru: [...] }
  const [loadingWords, setLoadingWords] = useState(false);
  const [selection, setSelection] = useState(null);  // { tStart, tEnd, text }
  const [activeClipId, setActiveClipId] = useState(null);
  const [working, setWorking] = useState(false);
  const [filter, setFilter] = useState('');
  const trTextRef = useRef(null);  // ref to .tr-text container for markers

  const reload = useCallback(async () => {
    try {
      const [ep, m] = await Promise.all([
        api.getEpisode(epId),
        api.getTranscript(epId).catch(() => null),
      ]);
      setEpisode(ep);
      setMeta(m);
    } catch (err) {
      toast(err.message, 'err');
    }
  }, [epId, toast]);

  useEffect(() => { reload(); }, [reload]);

  // Lazily fetch the word array for the active language (cached per language).
  useEffect(() => {
    if (!meta) return;
    if (lang === 'ru' && !meta.has_ru) return;
    if (wordsByLang[lang]) return;
    let cancelled = false;
    setLoadingWords(true);
    api.getTranscriptWords(epId, lang)
      .then((res) => {
        if (cancelled) return;
        setWordsByLang((prev) => ({ ...prev, [lang]: res.words || [] }));
      })
      .catch((err) => { if (!cancelled) toast(err.message, 'err'); })
      .finally(() => { if (!cancelled) setLoadingWords(false); });
    return () => { cancelled = true; };
  }, [epId, lang, meta, wordsByLang, toast]);

  const words = wordsByLang[lang] || [];

  // Flat list of clips with their assigned palette hue, sorted by start.
  const clips = useMemo(() => {
    if (!episode) return [];
    const all = [
      ...(episode.chapters || []).flatMap((c) => c.clips || []),
      ...(episode.orphan_clips || []),
    ];
    const sorted = [...all].sort((a, b) => Number(a.t_start) - Number(b.t_start));
    return sorted.map((c, i) => ({ ...c, _hue: CLIP_HUES[i % CLIP_HUES.length] }));
  }, [episode]);

  // wordIndex → array of clips whose time-range covers this word.
  const wordClipMap = useMemo(() => {
    if (!words.length) return new Map();
    const map = new Map();
    for (const clip of clips) {
      const startMs = clip.t_start * 1000;
      const endMs = clip.t_end * 1000;
      for (let i = 0; i < words.length; i += 1) {
        const w = words[i];
        if (w.start >= startMs && w.end <= endMs) {
          let arr = map.get(i);
          if (!arr) { arr = []; map.set(i, arr); }
          arr.push(clip);
        } else if (w.start > endMs) {
          break;
        }
      }
    }
    return map;
  }, [words, clips]);

  // Group words by chapter for rendering.
  const chapters = useMemo(() => {
    if (!words.length || !episode) return [];
    const epChapters = episode.chapters || [];
    const result = epChapters.map((ch) => ({ chapter: ch, words: [] }));
    const orphanChunk = { chapter: null, words: [] };

    for (let i = 0; i < words.length; i += 1) {
      const w = words[i];
      const sec = w.start / 1000;
      let placed = false;
      for (const c of result) {
        if (sec >= c.chapter.t_start && sec <= c.chapter.t_end) {
          c.words.push({ ...w, _idx: i });
          placed = true;
          break;
        }
      }
      if (!placed) orphanChunk.words.push({ ...w, _idx: i });
    }
    if (orphanChunk.words.length) result.push(orphanChunk);
    return result;
  }, [words, episode]);

  const handleSelection = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    let firstStart = Infinity;
    let lastEnd = -Infinity;

    const consider = (el) => {
      if (!el) return;
      const start = parseFloat(el.dataset.start);
      const end = parseFloat(el.dataset.end);
      if (Number.isFinite(start) && start < firstStart) firstStart = start;
      if (Number.isFinite(end) && end > lastEnd) lastEnd = end;
    };

    // Scope the scan to the selection's common ancestor (a chapter paragraph),
    // not the whole document — keeps this cheap even with 30k+ words.
    const scopeNode = range.commonAncestorContainer;
    const scope = scopeNode.nodeType === Node.ELEMENT_NODE ? scopeNode : scopeNode.parentElement;
    const candidates = scope?.querySelectorAll?.('.tr-word');
    if (candidates && candidates.length) {
      for (const w of candidates) {
        if (range.intersectsNode(w)) consider(w);
      }
    }
    // Always include the boundary words (covers single-word selections too).
    consider(nearestWordEl(range.startContainer));
    consider(nearestWordEl(range.endContainer));

    if (firstStart === Infinity || lastEnd === -Infinity) return;
    setSelection({
      tStart: firstStart / 1000,
      tEnd: lastEnd / 1000,
      text: range.toString().trim(),
    });
  }, []);

  const clearSelection = () => {
    window.getSelection()?.removeAllRanges();
    setSelection(null);
  };

  // Note: selection is driven purely by the native text selection (handleSelection).
  // The old draggable start/end markers were removed — to adjust a range, just
  // re-select the text.

  const switchLang = (next) => {
    if (next === lang) return;
    clearSelection();
    setLang(next);
  };

  const createFromSelection = async () => {
    if (!selection) return;
    setWorking(true);
    try {
      const clip = await api.createClip({
        episode_id: epId,
        t_start: selection.tStart,
        t_end: selection.tEnd,
        title: selection.text.slice(0, 60) || 'Manual clip',
      });
      toast(`клип создан · ${fmtDur(selection.tEnd - selection.tStart)}`, 'ok');
      clearSelection();
      navigate(`/clip/${clip.id}`);
    } catch (err) {
      toast(err.message, 'err');
    } finally {
      setWorking(false);
    }
  };

  const updateActiveClipRange = async () => {
    if (!selection || !activeClipId) return;
    setWorking(true);
    try {
      await api.updateClip(activeClipId, {
        t_start: selection.tStart,
        t_end: selection.tEnd,
      });
      toast(`границы обновлены · ${fmtDur(selection.tEnd - selection.tStart)}`, 'ok');
      try { await api.refreshSubtitles?.(activeClipId); } catch {}
      clearSelection();
      setActiveClipId(null);
      reload();
    } catch (err) {
      toast(err.message, 'err');
    } finally {
      setWorking(false);
    }
  };

  if (!episode) {
    return <div className="screen"><div className="empty-state"><div className="empty-icon">◇</div><div className="empty-title">загрузка эпизода…</div></div></div>;
  }
  if (!meta || (!meta.has_es && !meta.has_ru)) {
    return <div className="screen"><div className="empty-state"><div className="empty-icon">◇</div><div className="empty-title">транскрипт ещё не готов</div></div></div>;
  }

  const activeClip = activeClipId ? clips.find((c) => c.id === activeClipId) : null;
  const visibleClips = filter
    ? clips.filter((c) => (c.title || '').toLowerCase().includes(filter.toLowerCase()))
    : clips;
  const activeWordCount = lang === 'ru' ? (meta.word_count_ru || 0) : (meta.word_count || 0);

  return (
    <div className="screen tr-screen">
      <div className="tr-layout">
        <div className="tr-main-col">
          <div className="tr-toolbar">
            <button className="back" onClick={() => navigate('/episodes')}>← эпизоды</button>
            <span className="crumb-sep">/</span>
            <span className="crumb-current crumb-strong">{episode.title}</span>

            <div className="btn-group" style={{ marginLeft: 12 }}>
              <button
                className={`btn btn-compact ${lang === 'es' ? 'btn-dark' : 'btn-ghost'}`}
                onClick={() => switchLang('es')}
              >
                ES
              </button>
              {meta.has_ru && (
                <button
                  className={`btn btn-compact ${lang === 'ru' ? 'btn-dark' : 'btn-ghost'}`}
                  onClick={() => switchLang('ru')}
                >
                  RU
                </button>
              )}
            </div>

            <span className="grow" />
            <span className="muted small">
              {activeWordCount} слов · {clips.length} клипов
            </span>
            <button className="btn btn-outline" onClick={() => navigate(`/episode/${epId}/chapters`)}>📋 темы</button>
            <button className="btn btn-dark" onClick={() => navigate(`/episode/${epId}/new-clip`)}>+ клип</button>
            <span className="muted small" title="выдели текст — создай рилс · кликни на цвет — управляй клипом">ⓘ</span>
          </div>
          <div
            ref={trTextRef}
            className="tr-text"
            onMouseUp={handleSelection}
            onTouchEnd={handleSelection}
          >
            {loadingWords && !words.length && (
              <div className="muted small" style={{ padding: '8px 2px' }}>загрузка текста…</div>
            )}
            {chapters.map(({ chapter, words: chapterWords }, idx) => (
              <TranscriptChapter
                key={chapter?.id || `orphan-${idx}`}
                chapter={chapter}
                words={chapterWords}
                idx={idx}
                wordClipMap={wordClipMap}
                activeClipId={activeClipId}
                onWordClick={(id) => setActiveClipId(id === activeClipId ? null : id)}
                onWordDoubleClick={(id) => navigate(`/clip/${id}`)}
              />
            ))}
          </div>
        </div>

        <aside className="tr-aside">
          {selection ? (
            <div className="tr-selection-card">
              <div className="tr-selection-head">
                {activeClip ? '🔧 изменить границы' : '✂ выделено'}
              </div>
              <div className="tr-selection-meta">
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmtClock(selection.tStart)} → {fmtClock(selection.tEnd)}
                </span>
                <span className="muted small">{fmtDur(selection.tEnd - selection.tStart)}</span>
              </div>
              <div className={`tr-selection-preview ${lang}`}>
                «{selection.text.slice(0, 220)}{selection.text.length > 220 ? '…' : ''}»
              </div>
              {activeClip ? (
                <>
                  <div className="muted small" style={{ marginBottom: 8 }}>
                    клип <b>«{activeClip.title?.slice(0, 40)}»</b><br/>
                    было: {fmtClock(activeClip.t_start)} → {fmtClock(activeClip.t_end)}
                  </div>
                  <button
                    className="btn btn-publish btn-full"
                    onClick={updateActiveClipRange}
                    disabled={working || selection.tEnd - selection.tStart < 2}
                  >
                    {working ? '…' : 'обновить границы клипа'}
                  </button>
                  <button
                    className="btn btn-ghost btn-full"
                    style={{ marginTop: 6 }}
                    onClick={() => { setActiveClipId(null); clearSelection(); }}
                  >
                    отменить
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-publish btn-full"
                  onClick={createFromSelection}
                  disabled={working || selection.tEnd - selection.tStart < 2}
                >
                  {working ? '…' : 'создать рилс →'}
                </button>
              )}
            </div>
          ) : activeClip ? (
            <ActiveClipCard
              clip={activeClip}
              onClose={() => setActiveClipId(null)}
              onOpen={() => navigate(`/clip/${activeClip.id}`)}
              onRename={async (title) => {
                try {
                  await api.updateClip(activeClip.id, { title });
                  toast('переименовано', 'ok');
                  reload();
                } catch (err) { toast(err.message, 'err'); }
              }}
              onDelete={async () => {
                if (!window.confirm(`Удалить клип «${activeClip.title}»?`)) return;
                try {
                  await api.deleteClip(activeClip.id);
                  toast('клип удалён', 'ok');
                  setActiveClipId(null);
                  reload();
                } catch (err) { toast(err.message, 'err'); }
              }}
            />
          ) : (
            <div className="tr-selection-card empty">
              <div className="muted small">выдели текст слева — здесь появится кнопка «создать рилс».<br/>кликни на подсвеченный фрагмент — увидишь его клип.</div>
            </div>
          )}

          <div className="tr-clips-list">
            <div className="tr-clips-list-head">
              <h3 className="hand-h3" style={{ margin: 0 }}>Клипы эпизода ({clips.length})</h3>
              <input
                className="tr-clips-filter"
                type="search"
                placeholder="фильтр по названию…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
            {clips.length === 0 && <div className="muted small">пока пусто</div>}
            {visibleClips.length === 0 && clips.length > 0 && (
              <div className="muted small">по фильтру ничего</div>
            )}
            {visibleClips.map((c) => (
              <div
                key={c.id}
                className={'tr-clip-row' + (activeClipId === c.id ? ' active' : '')}
                onClick={() => setActiveClipId(c.id === activeClipId ? null : c.id)}
                style={c._hue ? { '--clip-line': c._hue.line, '--clip-fill': c._hue.fill } : undefined}
              >
                <span className="tr-clip-badge" style={c._hue ? { background: c._hue.line } : undefined}>{c.ordinal ?? '·'}</span>
                <div className="grow">
                  <div className="tr-clip-title">{c.title || '(без названия)'}</div>
                  <div className="muted small">
                    {fmtClock(c.t_start)} · {fmtDur(c.t_end - c.t_start)} · {STATUS_LABELS[c.status] || c.status}
                  </div>
                </div>
                <button
                  className="btn-link small"
                  onClick={(e) => { e.stopPropagation(); navigate(`/clip/${c.id}`); }}
                >
                  →
                </button>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
